import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChevronLeft, ChevronRight, Printer, MessageCircle, Plus, FileText } from 'lucide-react';

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

/**
 * Salary Report tab — month navigator, summary cards, per-staff payroll table.
 * Props:
 *   selectedMonth, setSelectedMonth
 *   payroll: { month, days_in_month, staff: [...], totals: {gross, deductions, net} } | null
 *   onChangeMonth: (delta) => void
 *   onPrint: () => void
 *   onWhatsappSummary: () => void
 *   onStaffClick: (row) => void
 *   onDeductClick: (row) => void
 *   onWhatsappSlip: (row) => void
 *   onDownloadPDF: (row) => void
 */
const SalaryReportTab = ({
  selectedMonth, setSelectedMonth, payroll,
  onChangeMonth, onPrint, onWhatsappSummary,
  onStaffClick, onDeductClick, onWhatsappSlip, onDownloadPDF,
}) => {
  const monthLabel = new Date(selectedMonth + '-01').toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });

  return (
    <>
      {/* Top bar */}
      <Card className="p-4 border-slate-200">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => onChangeMonth(-1)} data-testid="prev-month-salary">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}
              className="w-48 text-center" data-testid="salary-month-input" />
            <Button variant="outline" size="sm" onClick={() => onChangeMonth(1)} data-testid="next-month-salary">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onPrint} data-testid="print-salary-btn">
              <Printer className="h-4 w-4 mr-2" />Print
            </Button>
            <Button className="bg-green-600 hover:bg-green-700 text-white" onClick={onWhatsappSummary} data-testid="whatsapp-payroll-summary-btn">
              <MessageCircle className="h-4 w-4 mr-2" />WhatsApp Summary
            </Button>
          </div>
        </div>
      </Card>

      {/* Summary cards */}
      {payroll && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Card className="p-4 border-slate-200">
            <div className="text-xs text-slate-500 uppercase">Month</div>
            <div className="text-lg font-semibold text-slate-900">{monthLabel}</div>
            <div className="text-xs text-slate-500 mt-1">{payroll.days_in_month} days</div>
          </Card>
          <Card className="p-4 border-slate-200 bg-blue-50/60">
            <div className="text-xs text-blue-700 uppercase">Gross Payable</div>
            <div className="text-2xl font-bold text-blue-800">{fmtINR(payroll.totals.gross)}</div>
          </Card>
          <Card className="p-4 border-slate-200 bg-amber-50/60">
            <div className="text-xs text-amber-700 uppercase">Total Deductions</div>
            <div className="text-2xl font-bold text-amber-800">{fmtINR(payroll.totals.deductions)}</div>
          </Card>
          <Card className="p-4 border-slate-200 bg-emerald-50/60">
            <div className="text-xs text-emerald-700 uppercase">Net Payable</div>
            <div className="text-2xl font-bold text-emerald-800">{fmtINR(payroll.totals.net)}</div>
          </Card>
        </div>
      )}

      {/* Staff rows */}
      <Card className="border-slate-200 overflow-hidden" data-testid="salary-report-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-3 py-3 text-left font-medium text-slate-700">Staff</th>
                <th className="px-3 py-3 text-right font-medium text-slate-700">Salary/Mo</th>
                <th className="px-3 py-3 text-right font-medium text-slate-700">Daily Wage</th>
                <th className="px-3 py-3 text-center font-medium text-green-700">P</th>
                <th className="px-3 py-3 text-center font-medium text-amber-700">H</th>
                <th className="px-3 py-3 text-center font-medium text-red-700">A</th>
                <th className="px-3 py-3 text-right font-medium text-blue-700">Gross</th>
                <th className="px-3 py-3 text-right font-medium text-amber-700">Deduct.</th>
                <th className="px-3 py-3 text-right font-medium text-emerald-700">Net</th>
                <th className="px-3 py-3 text-center font-medium text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(!payroll || payroll.staff.length === 0) ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-slate-500">No staff / payroll data for this month.</td></tr>
              ) : payroll.staff.map(r => (
                <tr key={r.staff_id} className="hover:bg-slate-50" data-testid={`payroll-row-${r.staff_id}`}>
                  <td className="px-3 py-3">
                    <button className="text-left hover:text-blue-600" onClick={() => onStaffClick(r)}>
                      <div className="font-medium text-slate-900">{r.staff_name}</div>
                      <div className="text-xs text-slate-500">{r.phone}{r.role ? ` • ${r.role}` : ''}</div>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right">{fmtINR(r.monthly_salary)}</td>
                  <td className="px-3 py-3 text-right text-slate-600">{fmtINR(r.daily_wage)}</td>
                  <td className="px-3 py-3 text-center"><span className="inline-block w-8 h-8 rounded-full bg-green-100 text-green-700 font-bold leading-8">{r.present}</span></td>
                  <td className="px-3 py-3 text-center"><span className="inline-block w-8 h-8 rounded-full bg-amber-100 text-amber-700 font-bold leading-8">{r.half_day}</span></td>
                  <td className="px-3 py-3 text-center"><span className="inline-block w-8 h-8 rounded-full bg-red-100 text-red-700 font-bold leading-8">{r.absent}</span></td>
                  <td className="px-3 py-3 text-right font-semibold text-blue-700">{fmtINR(r.gross_salary)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="font-semibold text-amber-700">{fmtINR(r.total_deduction)}</div>
                    {r.adjustments?.length > 0 && (
                      <div className="text-[10px] text-slate-400">{r.adjustments.length} entr{r.adjustments.length === 1 ? 'y' : 'ies'}</div>
                    )}
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-emerald-700">{fmtINR(r.net_payable)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 justify-center flex-wrap">
                      <Button size="sm" variant="outline" className="h-8 text-xs"
                        onClick={() => onDeductClick(r)} data-testid={`add-deduction-${r.staff_id}`}>
                        <Plus className="h-3 w-3 mr-1" />Deduct
                      </Button>
                      <Button size="sm" className="h-8 text-xs bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => onWhatsappSlip(r)} data-testid={`whatsapp-slip-${r.staff_id}`}>
                        <MessageCircle className="h-3 w-3 mr-1" />Slip
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                        onClick={() => onDownloadPDF && onDownloadPDF(r)} data-testid={`pdf-ledger-${r.staff_id}`}
                        title="Download monthly salary ledger PDF">
                        <FileText className="h-3 w-3 mr-1" />PDF
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
};

export default SalaryReportTab;
